import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { paymentsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, DollarSign, Clock, CheckCircle2, AlertCircle, LogOut, Lock, Mail, Phone, User } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_xalawnsplus/artifacts/gh7eo36y_a0d14065-cb1f-449b-a4f6-38140797df8a.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerPortal() {
  const [authTab, setAuthTab] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [portalToken, setPortalToken] = useState(localStorage.getItem('portal_token'));
  
  // Auth forms
  const [loginData, setLoginData] = useState({ identifier: '', password: '' });
  const [registerData, setRegisterData] = useState({ phone: '', email: '', password: '', confirmPassword: '' });
  const [resetData, setResetData] = useState({ identifier: '' });
  const [resetConfirmData, setResetConfirmData] = useState({ token: '', new_password: '' });
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    if (portalToken) {
      fetchMyInvoices();
    }
  }, [portalToken]);

  const fetchMyInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/portal/my-invoices`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setCustomerData(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Fetch error:', error);
      localStorage.removeItem('portal_token');
      setPortalToken(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.identifier || !loginData.password) {
      toast.error('Please enter your phone/email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/portal/login`, loginData);
      localStorage.setItem('portal_token', response.data.access_token);
      setPortalToken(response.data.access_token);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.phone && !registerData.email) {
      toast.error('Please enter a phone number or email');
      return;
    }
    if (!registerData.password) {
      toast.error('Please enter a password');
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/portal/register`, {
        phone: registerData.phone || null,
        email: registerData.email || null,
        password: registerData.password
      });
      localStorage.setItem('portal_token', response.data.access_token);
      setPortalToken(response.data.access_token);
      
      if (response.data.has_customer) {
        toast.success(`Welcome, ${response.data.customer_name}!`);
      } else {
        toast.success('Account created! Contact us to link your customer account.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!resetData.identifier) {
      toast.error('Please enter your phone or email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/request-reset`, resetData);
      toast.success('If an account exists, a reset code has been sent');
      setShowResetConfirm(true);
    } catch (error) {
      toast.error('Failed to request reset');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (!resetConfirmData.token || !resetConfirmData.new_password) {
      toast.error('Please enter the reset code and new password');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/portal/confirm-reset`, resetConfirmData);
      toast.success('Password reset successful! Please login.');
      setShowResetConfirm(false);
      setAuthTab('login');
      setResetData({ identifier: '' });
      setResetConfirmData({ token: '', new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    setPortalToken(null);
    setIsAuthenticated(false);
    setCustomerData(null);
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const response = await paymentsAPI.createCheckout({
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to initiate payment. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'status-pending',
      paid: 'status-paid',
      overdue: 'status-overdue'
    };
    return <span className={`status-badge ${statusStyles[status] || 'status-pending'}`}>{status}</span>;
  };

  // Loading state
  if (loading && portalToken) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-[#262626] bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="XA's Lawns" className="h-12" />
            </Link>
            <div className="flex items-center gap-4">
              {isAuthenticated && (
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-white"
                  data-testid="portal-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              )}
              <Link to="/" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-4">
            Customer <span className="text-lime-500">Portal</span>
          </h1>
          <p className="text-gray-400 text-lg">
            {isAuthenticated ? 'View and pay your invoices' : 'Sign in to view your invoices'}
          </p>
        </div>

        {!isAuthenticated ? (
          /* Auth Forms */
          <div className="card-dark p-8 max-w-md mx-auto">
            <Tabs value={authTab} onValueChange={setAuthTab}>
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-[#262626]">
                <TabsTrigger value="login" className="data-[state=active]:bg-lime-500 data-[state=active]:text-black">Login</TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-lime-500 data-[state=active]:text-black">Register</TabsTrigger>
                <TabsTrigger value="reset" className="data-[state=active]:bg-lime-500 data-[state=active]:text-black">Reset</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label className="text-white">Phone or Email</Label>
                    <div className="relative mt-2">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        value={loginData.identifier}
                        onChange={(e) => setLoginData({...loginData, identifier: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="Phone or email"
                        data-testid="portal-login-identifier"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white">Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="••••••••"
                        data-testid="portal-login-password"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="portal-login-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-white">Phone Number</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        type="tel"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="(318) 222-3333"
                        data-testid="portal-register-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white">Email (Optional)</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="your@email.com"
                        data-testid="portal-register-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white">Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="••••••••"
                        data-testid="portal-register-password"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white">Confirm Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        type="password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                        className="input-dark pl-10"
                        placeholder="••••••••"
                        data-testid="portal-register-confirm"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="portal-register-btn">
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                  <p className="text-gray-500 text-xs text-center">
                    Use the phone number or email associated with your customer account.
                  </p>
                </form>
              </TabsContent>

              {/* Reset Tab */}
              <TabsContent value="reset">
                {!showResetConfirm ? (
                  <form onSubmit={handleRequestReset} className="space-y-4">
                    <div>
                      <Label className="text-white">Phone or Email</Label>
                      <div className="relative mt-2">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <Input
                          value={resetData.identifier}
                          onChange={(e) => setResetData({...resetData, identifier: e.target.value})}
                          className="input-dark pl-10"
                          placeholder="Phone or email on your account"
                          data-testid="portal-reset-identifier"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="portal-reset-request-btn">
                      {loading ? 'Sending...' : 'Send Reset Code'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleConfirmReset} className="space-y-4">
                    <div>
                      <Label className="text-white">Reset Code</Label>
                      <Input
                        value={resetConfirmData.token}
                        onChange={(e) => setResetConfirmData({...resetConfirmData, token: e.target.value.toUpperCase()})}
                        className="input-dark mt-2"
                        placeholder="Enter code from SMS/email"
                        data-testid="portal-reset-code"
                      />
                    </div>
                    <div>
                      <Label className="text-white">New Password</Label>
                      <div className="relative mt-2">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <Input
                          type="password"
                          value={resetConfirmData.new_password}
                          onChange={(e) => setResetConfirmData({...resetConfirmData, new_password: e.target.value})}
                          className="input-dark pl-10"
                          placeholder="••••••••"
                          data-testid="portal-reset-new-password"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="portal-reset-confirm-btn">
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="w-full text-gray-400"
                      onClick={() => setShowResetConfirm(false)}
                    >
                      Back
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          /* Customer Dashboard */
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl font-bold text-white uppercase">
                  Welcome{customerData?.customer_name ? `, ${customerData.customer_name}` : ''}
                </h2>
                <p className="text-gray-400">Your invoices and payment history</p>
              </div>
            </div>

            {/* Payment Methods Info */}
            <div className="card-dark p-6">
              <h3 className="font-heading text-lg font-bold text-white uppercase mb-4">
                Payment Options
              </h3>
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#262626]">
                  <CreditCard className="w-5 h-5 text-lime-500" />
                  <span className="text-gray-300">Credit Card</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#262626]">
                  <DollarSign className="w-5 h-5 text-lime-500" />
                  <span className="text-gray-300">Cash App</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#262626]">
                  <DollarSign className="w-5 h-5 text-lime-500" />
                  <span className="text-gray-300">Venmo</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#262626]">
                  <DollarSign className="w-5 h-5 text-lime-500" />
                  <span className="text-gray-300">Cash</span>
                </div>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                Pay online with credit card or contact us for Cash App, Venmo, or cash payment arrangements.
              </p>
            </div>

            {/* Invoices */}
            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold text-white uppercase">Your Invoices</h3>
              
              {!customerData?.customer_name ? (
                <div className="card-dark p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-2">Your portal account is not yet linked to a customer account.</p>
                  <p className="text-gray-500 text-sm">Please contact us to link your account so you can view your invoices.</p>
                </div>
              ) : customerData?.invoices?.length === 0 ? (
                <div className="card-dark p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-lime-500 mx-auto mb-4" />
                  <p className="text-gray-400">No invoices found. You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customerData?.invoices?.map((invoice) => (
                    <Card key={invoice.id} className="bg-[#171717] border-[#262626]" data-testid={`invoice-card-${invoice.id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-heading text-lg font-bold text-white">
                                {invoice.invoice_number}
                              </span>
                              {getStatusBadge(invoice.status)}
                            </div>
                            <p className="text-gray-400 mb-1">{invoice.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                Due: {new Date(invoice.due_date).toLocaleDateString()}
                              </span>
                              {invoice.service_date && (
                                <span>Service: {new Date(invoice.service_date).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-heading text-2xl font-bold text-lime-500">
                                ${invoice.amount.toFixed(2)}
                              </p>
                            </div>
                            
                            {invoice.status === 'pending' && (
                              <Button 
                                onClick={() => handlePayInvoice(invoice.id)}
                                className="btn-primary"
                                data-testid={`pay-invoice-${invoice.id}`}
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay Now
                              </Button>
                            )}
                            
                            {invoice.status === 'paid' && (
                              <div className="flex items-center gap-2 text-lime-500">
                                <CheckCircle2 className="w-5 h-5" />
                                <span>Paid</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {invoice.status === 'overdue' && (
                          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                            <span className="text-red-400 text-sm">This invoice is overdue. Please pay as soon as possible.</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
